// useProfilePrivacy Hook
// Manages privacy checks and enforcement for profile sections

import { useCallback, useMemo } from 'react';
import { useUser } from '../../context/UserContext';

export const useProfilePrivacy = (profile, currentUserId, options = {}) => {
  const { user: currentUser, userClubs, userProfile } = useUser();
  const { previewMode = 'owner' } = options;

  // Get user's club IDs
  const userClubIds = useMemo(() => {
    if (!userClubs || !Array.isArray(userClubs)) return [];
    return userClubs.map(membership => membership.club?._id || membership.club);
  }, [userClubs]);

  // Check if users share any clubs
  const hasSharedClubs = useCallback((profileClubHistory) => {
    if (!profileClubHistory || !userClubIds.length) return false;
    
    return profileClubHistory.some(history => 
      userClubIds.includes(history.clubId || history.club?._id)
    );
  }, [userClubIds]);

  // Check if current user can view a section
  const canViewSection = useCallback((section, privacyLevel, profileData) => {
    // Check if user is the profile owner
    const isProfileOwner = 
      currentUserId === profileData?.userId?._id || 
      currentUserId === profileData?.userId;

    // If owner is in preview mode, simulate the selected viewer type
    if (isProfileOwner && previewMode !== 'owner') {
      // Simulate public viewer
      if (previewMode === 'public') {
        return privacyLevel === 'public';
      }
      
      // Simulate club member viewer
      if (previewMode === 'club') {
        return privacyLevel === 'public' || privacyLevel === 'clubs';
      }
    }

    // Owner in normal mode can always see everything
    if (isProfileOwner) {
      return true;
    }

    // Public sections are always visible
    if (privacyLevel === 'public') {
      return true;
    }

    // Private sections only visible to owner
    if (privacyLevel === 'private') {
      return false;
    }

    // Club-only sections require shared club membership
    if (privacyLevel === 'clubs') {
      return hasSharedClubs(profileData?.clubHistory);
    }

    return false;
  }, [currentUserId, hasSharedClubs, previewMode]);

  // Get allowed sections based on privacy settings
  const getAllowedSections = useCallback((privacySettings, profileData) => {
    if (!privacySettings) return [];

    const sections = [];
    
    for (const [section, level] of Object.entries(privacySettings)) {
      if (canViewSection(section, level, profileData)) {
        sections.push(section);
      }
    }
    
    return sections;
  }, [canViewSection]);

  // Check if user is authenticated
  const isAuthenticated = useMemo(() => {
    // User is authenticated if we have either:
    // 1. currentUserId passed from parent (MongoDB ID)
    // 2. userProfile with _id from context
    // 3. currentUser (Firebase user object)
    return !!(currentUserId || userProfile?._id || currentUser);
  }, [currentUser, currentUserId, userProfile]);

  // Get privacy icon based on level
  const getPrivacyIcon = useCallback((level) => {
    switch (level) {
      case 'public':
        return 'FaGlobe';
      case 'clubs':
        return 'FaUsers';
      case 'private':
        return 'FaLock';
      default:
        return 'FaLock';
    }
  }, []);

  // Get privacy description
  const getPrivacyDescription = useCallback((level) => {
    switch (level) {
      case 'public':
        return 'Visible to everyone';
      case 'clubs':
        return 'Visible to club members only';
      case 'private':
        return 'Only visible to you';
      default:
        return 'Private';
    }
  }, []);

  // Get privacy badge color
  const getPrivacyColor = useCallback((level) => {
    switch (level) {
      case 'public':
        return '#10b981'; // green
      case 'clubs':
        return '#3b82f6'; // blue
      case 'private':
        return '#ef4444'; // red
      default:
        return '#6b7280'; // gray
    }
  }, []);

  // Check if section contains sensitive data
  const isSensitiveSection = useCallback((section) => {
    const sensitiveSections = [
      'contactPreferences',
      'availability',
      'events',
      'analytics'
    ];
    return sensitiveSections.includes(section);
  }, []);

  // Get recommended privacy level for a section
  const getRecommendedPrivacy = useCallback((section) => {
    if (isSensitiveSection(section)) {
      return 'private';
    }
    
    const publicSections = ['basicInfo', 'athleticProfile', 'skills', 'awards'];
    if (publicSections.includes(section)) {
      return 'public';
    }
    
    return 'clubs';
  }, [isSensitiveSection]);

  return {
    // State
    isAuthenticated,
    userClubIds,
    
    // Methods
    canViewSection,
    getAllowedSections,
    hasSharedClubs,
    getPrivacyIcon,
    getPrivacyDescription,
    getPrivacyColor,
    isSensitiveSection,
    getRecommendedPrivacy
  };
};
